import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  padding: 0.5rem 1rem;
`;

const DropdownList = styled.ul<{open: boolean}>`
    display: ${props => props.open ? "block" : "none"};
    position: absolute;
    right: 0;
    top: 100%;
    background-color: var(--content-box-bg);
    border: 1px solid var(--content-box-border);
    margin: 0;
    padding: 0;
    list-style: none;
    min-width: 160px;
    max-height: 16rem;
    overflow-y: auto;
    z-index: 10;
`;

const SearchInput = styled.input`
    box-sizing: border-box;
    width: 100%;
    padding: 0.5rem;
    border: none;
    border-bottom: 1px solid var(--content-box-border);
    background-color: var(--content-box-bg);
    color: inherit;

    &:focus {
        outline: none;
    }
`;

const DropdownItem = styled.li`
  padding: 0.5rem 1rem;
  cursor: pointer;
  &:hover {
    background: var(--primary-blue-hover, #0074d9);
    color: #fff;
  }
`;

const NoResults = styled.li`
  padding: 0.5rem 1rem;
  opacity: 0.6;
`;

interface CustomSelectProps {
    options: string[];
    onSelect: (value: string) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
}

export function CustomSelect({options, onSelect, open, setOpen}: CustomSelectProps) {
    const [selected, setSelected] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, setOpen]);

    useEffect(() => {
        if (open) {
            setSearch('');
            setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [open]);

    const filteredOptions = options.filter(option => option.toLowerCase().includes(search.toLowerCase()));

    return (
        <DropdownContainer ref={containerRef}>
            <DropdownList open={open}>
                {options.length > 5 && (
                    <li>
                        <SearchInput
                            ref={searchRef}
                            type={'text'}
                            placeholder={'Search...'}
                            value={search}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && filteredOptions.length === 1) {
                                    setSelected(filteredOptions[0]);
                                    setOpen(false);
                                    onSelect(filteredOptions[0]);
                                } else if (e.key === 'Escape') {
                                    setOpen(false);
                                }
                            }}
                        />
                    </li>
                )}
                {filteredOptions.map(option => (
                    <DropdownItem
                        key={option}
                        onClick={() => {
                            setSelected(option);
                            setOpen(false);
                            onSelect(option);
                        }}
                    >
                        {option}
                    </DropdownItem>
                ))}
                {filteredOptions.length === 0 && (<NoResults>No matches</NoResults>)}
            </DropdownList>
        </DropdownContainer>
    );
}
